/**
 * Form delivery providers, tried in this order: Jotform → HubSpot → Formspree → email (FormSubmit, default).
 * Configure in /assets/site-config.js; see docs/WEBSITE-SETTINGS-GUIDE.md.
 */
import type { JotformFieldMap, SiteConfig } from '../../../config/site-config';
import { asString, isRecord } from '../../../lib/guards';
import { isConfigured } from '../../../lib/links';
import { readableFieldName, toReadableFields } from '../field-labels';
import { FormDeliveryError, type DeliveryRequest } from './delivery-types';

export interface DeliveryProvider {
  readonly name: string;
  send(request: DeliveryRequest): Promise<void>;
}

const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' } as const;

function ensureOk(provider: string, response: Response): void {
  if (!response.ok) throw new FormDeliveryError(provider, `HTTP ${response.status}`);
}

function jotformProvider(formId: string, fields: JotformFieldMap): DeliveryProvider {
  return {
    name: 'Jotform',
    async send({ payload }) {
      const body = new FormData();
      const unmapped: string[] = [];
      Object.entries(payload).forEach(([key, value]) => {
        if (!value) return;
        const target = fields[key];
        if (target) body.append(target, value);
        else unmapped.push(`${readableFieldName(key)}: ${value}`);
      });
      const extraField = fields._extra;
      if (extraField && unmapped.length) body.append(extraField, unmapped.join('\n'));
      // Jotform's endpoint does not return a readable response to other sites (no-cors), so success is
      // assumed once the request is sent. Test with a real submission after setup.
      await fetch(`https://submit.jotform.com/submit/${formId}/`, { method: 'POST', mode: 'no-cors', body });
    },
  };
}

function hubspotProvider(portalId: string, formId: string): DeliveryProvider {
  return {
    name: 'HubSpot',
    async send({ payload }) {
      const fields = Object.entries(payload)
        .filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '')
        .map(([name, value]) => ({ name, value }));
      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields, context: { pageUri: location.href, pageName: document.title } }),
      });
      ensureOk('HubSpot', response);
    },
  };
}

function formspreeProvider(formId: string): DeliveryProvider {
  return {
    name: 'Formspree',
    async send({ payload, subject, replyTo }) {
      const response = await fetch(`https://formspree.io/f/${formId}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ _subject: subject, _replyto: replyTo, ...toReadableFields(payload) }),
      });
      ensureOk('Formspree', response);
    },
  };
}

function formSubmitProvider(address: string): DeliveryProvider {
  return {
    name: 'FormSubmit',
    async send({ payload, subject, replyTo }) {
      const response = await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(address)}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          _subject: subject,
          _replyto: replyTo,
          _template: 'table',
          _captcha: 'false',
          ...toReadableFields(payload),
        }),
      });
      const result: unknown = await response.json().catch(() => null);
      const body = isRecord(result) ? result : {};
      if (!response.ok || String(body.success) !== 'true') {
        throw new FormDeliveryError('FormSubmit', asString(body.message) || `HTTP ${response.status}`);
      }
    },
  };
}

/** Opens the visitor's email app with the submission. Used when no delivery service is configured. */
function emailClientProvider(open: (request: DeliveryRequest) => void): DeliveryProvider {
  return {
    name: 'Email app',
    send(request) {
      open(request);
      return Promise.resolve();
    },
  };
}

const PREVIEW_SAVE_DELAY_MS = 900;

/** Single-file preview: nothing is sent, the save is simulated. */
const previewProvider: DeliveryProvider = {
  name: 'Preview',
  send: () =>
    new Promise((resolve) => {
      setTimeout(resolve, PREVIEW_SAVE_DELAY_MS);
    }),
};

export function selectProvider(
  config: SiteConfig,
  openEmailClient: (request: DeliveryRequest) => void,
): DeliveryProvider {
  if (isConfigured(config.JOTFORM_FORM_ID) && config.JOTFORM_FIELDS)
    return jotformProvider(config.JOTFORM_FORM_ID, config.JOTFORM_FIELDS);
  if (isConfigured(config.HUBSPOT_PORTAL_ID) && isConfigured(config.HUBSPOT_FORM_ID)) {
    return hubspotProvider(config.HUBSPOT_PORTAL_ID, config.HUBSPOT_FORM_ID);
  }
  if (isConfigured(config.FORMSPREE_ID)) return formspreeProvider(config.FORMSPREE_ID);
  if (config.ROUTING !== 'path') return previewProvider;
  if (isConfigured(config.FORM_EMAIL)) return formSubmitProvider(config.FORM_EMAIL_ALIAS || config.FORM_EMAIL);
  return emailClientProvider(openEmailClient);
}
